// src/oura/lib/answerText.ts
//
// A stored engine answer, cleaned for reading (#3800, handoff §4). Runs at
// render, so it applies to every answer already in the store.
//
// Two problems in the stored text, both measured on the pipeline's own
// captures (819 answers):
//
//   1. 761 of them (92%) began with the Google Maps carousel ChatGPT draws
//      above its reply — the widget's labels, unspaced, in front of the answer:
//        "Aessence Clinic Ara Damansara★ 4.9•Skin care clinicClosedDr. A Clinic…"
//   2. All 819 used single newlines only, so Markdown joined an eight-clinic
//      list into one wall of text.
//   3. Every app answer ends with the ChatGPT page's own composer — see
//      `stripAppChrome`. Interface, not evidence.
//
// WHAT THIS FILE WILL NOT DO is invent structure. Today's app captures arrive
// with NO newline at all (20 of 20 on THE LUSH CLINIC's 29 Sep scan; Gemini's
// answers to the same questions carry 26 to 46), so they render as one
// paragraph however they are cleaned. Splitting that wall on guessed sentence
// boundaries would put paragraph breaks where the model wrote none, which is a
// worse lie than a dense paragraph. The capture is the place to fix it —
// filed as ourteam-backend#5133.
//
// THE CAROUSEL IS DETECTED, NEVER ASSUMED. The handoff describes the fix as
// "dropping that line", which was safe while every stored answer began with
// the widget. ourteam-backend#4938 changes the capture: new ChatGPT-app answers
// begin with the model's own first sentence, and an unconditional drop would
// delete the first real paragraph of every one of them. So line 1 goes only
// when it carries the widget's furniture — a star rating immediately followed
// by the "•" before a category, or the map's own attribution lines.

/**
 * The carousel's furniture. Each alternative is something a written answer
 * does not produce:
 *   `★ 4.9•` / `★4.9 •`   rating glued to the category bullet
 *   `© Mapbox`, `© OpenStreetMap`, `Map data is currently unavailable`,
 *   `Improve this map`      the map tile's own chrome
 * A prose "rated 4.9 stars" or "5.0/5 from 69 reviews" matches none of them.
 */
const CAROUSEL = /★\s?\d(?:\.\d)?\s*•|©\s?Mapbox|©\s?OpenStreetMap|Map data is currently unavailable|Improve this map/;

/** True when `line` is the Maps carousel rather than a line of the answer. */
export function isMapsCarousel(line: string): boolean {
    return CAROUSEL.test(line);
}

/**
 * The carousel as a span inside a longer line: from the map's own controls
 * ("Reset view" / "Expand map") to its attribution ("© OpenStreetMap" and the
 * "Improve this map" link that follows it, or that link alone). Lazy, so two
 * widgets in one answer are cut separately and the prose between them stays.
 */
const CAROUSEL_SPAN = /(?:Reset view|Expand map)[\s\S]*?(?:©\s?OpenStreetMap(?:\s*Improve this map)?|Improve this map)/g;

/** `text` without the carousel span(s), the prose either side joined by one space; unchanged when none is found. */
function stripCarouselSpan(text: string): string {
    if (!CAROUSEL.test(text)) return text;
    const cut = text.replace(CAROUSEL_SPAN, " ").replace(/[ \t]{2,}/g, " ").trim();
    return cut;
}

/**
 * The ChatGPT app's own composer, captured after the answer (#3945 QA).
 *
 * Every app answer ends with the furniture of the page it was read from — the
 * signed-in initials, the Sources button, and the empty composer's prompt:
 *
 *   "… I can narrow it down. AMSourcesWhat are you working on?Where should we
 *    begin?You'll get smarter responses and can upload files, images, and
 *    more.Log inSign up for freeCameraPhotosFilesAdd photosLog in to add more
 *    photosWeb search"
 *
 * 20 of 20 stored answers on THE LUSH CLINIC's 29 Sep scan carry it, and it is
 * roughly 200 characters of interface on the end of every one. It is not the
 * model's words, so it is not evidence, and a reader comparing our capture
 * with their own would find it in neither.
 *
 * ANCHORED, NEVER GREEDY. The cut starts at the Sources button or at the
 * composer's first question, and only when the composer's own sentence follows
 * it — a model that happened to write "What are you working on?" in an answer
 * does not lose the rest of its reply.
 */
const APP_CHROME = [
    // The composer, with the Sources button and the signed-in initials before it.
    /\s*(?:[A-Z]{2,3})?Sources?(?=What are you working on\?)/,
    /\s*What are you working on\?(?=Where should we begin\?)/,
    // The signed-out panel, which sits further up than the composer does.
    /\s*(?:[A-Z]{2,3})?Sources?(?=Log in for advice and recommendations)/,
    /\s*Log in for advice and recommendations tailored to you\./,
];

/**
 * Interface copy that must appear AFTER an anchor, and be something other than
 * the anchor itself, for the cut to happen (Jack, #3944).
 *
 * The first guard here asked whether the interface appeared anywhere in what
 * was about to be dropped — which the anchor satisfied on its own, so the
 * fourth anchor validated itself. An answer quoting "Log in for advice and
 * recommendations tailored to you." lost everything after that sentence: 134
 * characters of a 181-character answer, silently. Evidence, deleted by the
 * function whose contract is to leave the uncertain case alone.
 *
 * So corroboration is INDEPENDENT (read past the end of the anchor's own
 * match) and NEAR (within the window below). Near matters as much as
 * independent: a real footer 1,500 characters later must not license a cut at
 * a sentence that merely reads like one.
 */
const APP_CORROBORATION = /You[’']ll get smarter responses|Log in to add more photos|Log inSign up for free|What are you working on\?Where should we begin\?/;

/** The chrome runs about 200 characters. 400 is slack, not licence. */
const CORROBORATION_WINDOW = 400;

/**
 * The answer with the app's interface taken off the end, or unchanged.
 *
 * Cuts at the EARLIEST corroborated anchor, because the signed-out panel comes
 * before the composer and stopping at the later one would leave the first on
 * screen.
 */
export function stripAppChrome(text: string): string {
    const at = APP_CHROME.reduce((best, re) => {
        const m = re.exec(text);
        if (!m) return best;
        const after = text.slice(m.index + m[0].length, m.index + m[0].length + CORROBORATION_WINDOW);
        if (!APP_CORROBORATION.test(after)) return best;
        return best === -1 || m.index < best ? m.index : best;
    }, -1);
    return at === -1 ? text : text.slice(0, at).trimEnd();
}

/**
 * The answer as a reader should see it.
 *
 * - Line 1 is removed only when it is the carousel (see above).
 * - Single newlines become paragraph breaks, but only in a text that has no
 *   blank line anywhere. An answer that already has structure — every capture
 *   after ourteam-backend#4938, and every API answer — is left exactly as the
 *   model wrote it, lists and all.
 */
export function cleanAnswerText(raw: string): string {
    let text = (raw ?? "").replace(/\r\n?/g, "\n").trim();
    const nl = text.indexOf("\n");
    const first = nl === -1 ? text : text.slice(0, nl);
    if (nl === -1) {
        // The whole answer is one line, so "line 1 is the carousel" would delete
        // the answer with it (#4180: a 3,000-character answer rendered as empty).
        // Only the widget's own span goes. With no span to find, the text stays:
        // some map furniture on screen is better than an answer thrown away.
        text = stripCarouselSpan(text);
    } else if (isMapsCarousel(first)) {
        text = text.slice(nl + 1).trim();
    }
    text = stripAppChrome(text);
    if (!/\n\s*\n/.test(text)) text = text.replace(/\n+/g, "\n\n");
    return text;
}
